/**
 * Pixel layout of the company network view: x positions inside each row, row
 * heights, orthogonal edge routes and label positions, from measured node
 * sizes. Pure — CompanyNetworkView measures, calls layoutGraph, and renders.
 *
 * Readability rules (the v1 diagram's failure modes):
 *   - A box's links into one row share one branching line ("bus"); boxes
 *     whose only link goes to one box share one line into it. One label
 *     when they all say the same ("Geschäftsführer(in)" once, not seven
 *     times), else each label at its edge's far end. All lines have one
 *     weight; roles are dashed.
 *   - Horizontal segments in a gap get their own lane when they overlap, and
 *     the gap grows with its lane count instead of squeezing labels.
 *   - Labels sit at the end of an edge farther from the focal, where each one
 *     has its own node above or below it; colliding labels drop a level.
 */
import type { GraphEdge, GraphNode } from './model';

export interface Size {
  w: number;
  h: number;
}
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface EdgePath {
  id: string;
  d: string;
  dashed: boolean;
  width: number;
}
export interface EdgeLabel {
  id: string;
  x: number;
  y: number;
  text: string;
  /** 'center': centred on (x, y); 'side': left edge at x, beside a trunk. */
  anchor: 'center' | 'side';
  /** Edges this label stands for (several for a shared trunk). */
  edgeIds: string[];
  /** Collides with others wherever it goes: shown only on hover/selection. */
  hidden?: boolean;
}
/** A drawn stretch of line: every shared stretch once, so overlaps don't darken. */
export interface LineSegment {
  d: string;
  dashed: boolean;
}
export interface GraphLayout {
  boxes: Map<string, Box>;
  paths: EdgePath[];
  labels: EdgeLabel[];
  /**
   * The base drawing: all edge paths cut into straight stretches, overlapping
   * stretches of one style merged, so a stretch carrying several links looks
   * exactly like one carrying a single link. `paths` (per edge) are only
   * drawn on top for highlighting.
   */
  segments: LineSegment[];
  width: number;
  height: number;
}

const H_GAP = 28; // between boxes in a row
const PAD = 24; // around the drawing
const BAND = 40; // label band at each end of a row gap
const LANE = 8; // spacing of horizontal segments
const MIN_GAP = 72;
const LABEL_H = 18;
const PORT_INSET = 18;
/** Most a label band of one row gap may grow to make room for labels. */
const MAX_GROW = 180;
const LINE_W = 1.4;

const LINE_H = 15;
const lines = (text: string) => text.split('\n');
/** Measures one label line in px (first line semibold, others regular); estimated without one. */
export type MeasureText = (line: string, first: boolean) => number;
const estimate: MeasureText = (line) => line.length * 7;
const labelWidthWith = (measure: MeasureText) => (text: string) =>
  Math.ceil(Math.max(...lines(text).map((l, i) => measure(l, i === 0)))) + 14;
const labelHeight = (text: string) => LABEL_H + (lines(text).length - 1) * LINE_H;

/** Weighted isotonic regression (pool adjacent violators): non-decreasing fit. */
function isotonic(targets: number[], weights: number[]): number[] {
  const blocks: { v: number; w: number; n: number }[] = [];
  for (let i = 0; i < targets.length; i++) {
    blocks.push({ v: targets[i], w: weights[i], n: 1 });
    while (blocks.length > 1 && blocks[blocks.length - 2].v > blocks[blocks.length - 1].v) {
      const b = blocks.pop()!;
      const a = blocks.pop()!;
      const w = a.w + b.w;
      blocks.push({ v: (a.v * a.w + b.v * b.w) / w, w, n: a.n + b.n });
    }
  }
  const out: number[] = [];
  for (const b of blocks) for (let i = 0; i < b.n; i++) out.push(b.v);
  return out;
}

/**
 * Centres for one ordered row: as close to each node's desired centre as the
 * spacing allows. Nodes without a wish keep their place in the sequence.
 */
function placeRow(order: string[], size: Map<string, Size>, desired: Map<string, number>, fixed?: string): Map<string, number> {
  const offsets: number[] = [];
  let acc = 0;
  for (let i = 0; i < order.length; i++) {
    if (i > 0) acc += (size.get(order[i - 1])!.w + size.get(order[i])!.w) / 2 + H_GAP;
    offsets.push(acc);
  }
  // Seed nodes without a wish from their nearest neighbour that has one.
  const wish = order.map((id) => desired.get(id));
  const anyWish = wish.findIndex((w) => w !== undefined);
  const base = anyWish >= 0 ? wish[anyWish]! - offsets[anyWish] : -acc / 2;
  const targets: number[] = [];
  const weights: number[] = [];
  let last = base;
  for (let i = 0; i < order.length; i++) {
    const w = wish[i];
    if (w !== undefined) last = w - offsets[i];
    targets.push(w !== undefined ? w - offsets[i] : last);
    weights.push(order[i] === fixed ? 1e6 : w !== undefined ? 1 : 0.001);
  }
  const fit = isotonic(targets, weights);
  return new Map(order.map((id, i) => [id, fit[i] + offsets[i]]));
}

interface Attach {
  node: string;
  side: 'top' | 'bottom';
  dashed: boolean;
  /** Sort key: x of the far end(s). */
  toward: number;
  x?: number;
}

interface Item {
  edges: GraphEdge[];
  gap: number; // the row above the gap that carries the horizontal segment
  kind: 'out' | 'in' | 'one';
  label: string;
  dashed: boolean;
  width: number;
  hub: string | null;
  attach: Map<string, Attach>; // `${edgeId}:up` / `${edgeId}:down`, shared for the hub end
  lane: number;
  minX: number;
  maxX: number;
  /**
   * An edge spanning more than one row: it drops into the gap below its
   * source, runs to a free corridor `cx` between the boxes of the rows it
   * crosses, down that corridor, then across the gap above its target.
   * `laneA` is its lane in the source gap (none when the source port itself
   * is free all the way down).
   */
  skip?: { fromGap: number; cx: number; laneA: number | null };
}

type Grow = Map<number, { top: number; bottom: number }>;

/**
 * Lays out, then gives a row gap more height wherever a label found no free
 * spot along its line, and lays out again (a few rounds): labels slide along
 * their own line to make space instead of overlapping or hiding.
 */
export function layoutGraph(
  graphNodes: GraphNode[],
  edges: GraphEdge[],
  size: Map<string, Size>,
  focal: string,
  labelFor: (e: GraphEdge) => string,
  /** Rows of leaves wider than this wrap onto extra lines further out. */
  maxWidth?: number,
  measureText: MeasureText = estimate,
): GraphLayout {
  const grow: Grow = new Map();
  let pass = layoutPass(graphNodes, edges, size, focal, labelFor, maxWidth, measureText, grow);
  for (let round = 0; round < 4 && pass.need.size; round++) {
    let grew = false;
    for (const [g, n] of pass.need) {
      const cur = grow.get(g) ?? { top: 0, bottom: 0 };
      const next = { top: Math.min(MAX_GROW, cur.top + n.top), bottom: Math.min(MAX_GROW, cur.bottom + n.bottom) };
      if (next.top !== cur.top || next.bottom !== cur.bottom) grew = true;
      grow.set(g, next);
    }
    if (!grew) break;
    pass = layoutPass(graphNodes, edges, size, focal, labelFor, maxWidth, measureText, grow);
  }
  return pass.layout;
}

function layoutPass(
  graphNodes: GraphNode[],
  edges: GraphEdge[],
  size: Map<string, Size>,
  focal: string,
  labelFor: (e: GraphEdge) => string,
  maxWidth: number | undefined,
  measureText: MeasureText,
  grow: Grow,
): { layout: GraphLayout; need: Grow } {
  const growTop = (g: number) => grow.get(g)?.top ?? 0;
  const growBottom = (g: number) => grow.get(g)?.bottom ?? 0;
  const labelWidth = labelWidthWith(measureText);
  // A row of leaves (nothing further out hangs off it) that is wider than the
  // slot wraps onto up to three lines; the outer lines' edges then run down
  // corridors between the inner line's boxes.
  const rowOfId = new Map(graphNodes.map((n) => [n.id, n.row]));
  if (maxWidth) {
    const nb = new Map<string, string[]>();
    for (const e of edges) {
      if (!nb.has(e.up)) nb.set(e.up, []);
      if (!nb.has(e.down)) nb.set(e.down, []);
      nb.get(e.up)!.push(e.down);
      nb.get(e.down)!.push(e.up);
    }
    const byRow = new Map<number, string[]>();
    for (const n of graphNodes) {
      if (!byRow.has(n.row)) byRow.set(n.row, []);
      byRow.get(n.row)!.push(n.id);
    }
    for (const [r, ids] of byRow) {
      if (r === 0 || ids.length < 4) continue;
      const total = ids.reduce((sum, id) => sum + size.get(id)!.w, 0) + H_GAP * (ids.length - 1);
      if (total <= maxWidth) continue;
      const further = (v: string) => {
        const rv = rowOfId.get(v)!;
        return Math.sign(rv) === Math.sign(r) && Math.abs(rv) > Math.abs(r);
      };
      if (ids.some((id) => (nb.get(id) ?? []).some(further))) continue;
      const k = Math.min(3, Math.ceil(total / maxWidth));
      ids.forEach((id, i) => {
        if (i % k) rowOfId.set(id, r + (Math.sign(r) * (i % k)) / k);
      });
    }
  }
  // Filters can empty a row; close the hole so every gap has rows on both sides.
  const used = [...new Set(rowOfId.values())].sort((a, b) => a - b);
  const zero = Math.max(0, used.indexOf(0));
  const nodes = graphNodes.map((n) => ({ ...n, row: used.indexOf(rowOfId.get(n.id)!) - zero }));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const rowOf = (id: string) => byId.get(id)!.row;
  const rows = new Map<number, string[]>();
  for (const n of nodes) {
    if (!rows.has(n.row)) rows.set(n.row, []);
    rows.get(n.row)!.push(n.id);
  }
  const rowIds = [...rows.keys()].sort((a, b) => a - b);
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!adj.has(e.up)) adj.set(e.up, []);
    if (!adj.has(e.down)) adj.set(e.down, []);
    adj.get(e.up)!.push(e.down);
    adj.get(e.down)!.push(e.up);
  }

  // ── x: rows outward from the focal, each node under/over its neighbours
  // nearer the focal; two passes so row 0 can follow its neighbours too.
  const cx = new Map<string, number>();
  const outward = [...rowIds].sort((a, b) => Math.abs(a) - Math.abs(b) || b - a);
  for (let pass = 0; pass < 3; pass++) {
    for (const r of outward) {
      const ids = rows.get(r)!;
      const desired = new Map<string, number>();
      for (const id of ids) {
        if (id === focal) {
          desired.set(id, 0);
          continue;
        }
        const nb = (adj.get(id) ?? []).filter((v) => cx.has(v) && v !== id);
        const inner = nb.filter((v) => Math.abs(rowOf(v)) < Math.abs(r));
        const use = inner.length ? inner : nb.filter((v) => rowOf(v) !== r);
        if (use.length) desired.set(id, use.reduce((s, v) => s + cx.get(v)!, 0) / use.length);
      }
      // Order by wish (stable for nodes without one); the focal stays central.
      const prev = ids.map((id, i) => ({ id, i, d: desired.get(id) ?? cx.get(id) }));
      const known = prev.filter((p) => p.d !== undefined).sort((a, b) => a.d! - b.d! || a.i - b.i);
      const unknown = prev.filter((p) => p.d === undefined);
      let order = [...known.map((p) => p.id)];
      // Unplaced nodes split around the middle so the row stays balanced.
      unknown.forEach((p, k) => (k % 2 ? order.unshift(p.id) : order.push(p.id)));
      if (r === 0 && order.includes(focal)) {
        const rest = order.filter((id) => id !== focal);
        const left = rest.filter((id) => (desired.get(id) ?? 0) < 0);
        const right = rest.filter((id) => (desired.get(id) ?? 0) >= 0);
        // Without wishes, balance the sides.
        while (left.length < right.length - 1 && right.some((id) => !desired.has(id))) {
          const k = right.findIndex((id) => !desired.has(id));
          left.unshift(right.splice(k, 1)[0]);
        }
        order = [...left, focal, ...right];
      }
      rows.set(r, order);
      const placed = placeRow(order, size, desired, r === 0 ? focal : undefined);
      for (const [id, x] of placed) cx.set(id, x);
    }
  }

  // ── edges → items (shared trunks), then ports, lanes, gaps, y
  const items: Item[] = [];
  const dashedOf = (e: GraphEdge) => !e.capital;
  // one weight for every line; roles are told apart by the dash, shares by the label
  const widthOf = () => LINE_W;
  const gapOf = (e: GraphEdge) => {
    const ru = rowOf(e.up);
    const rd = rowOf(e.down);
    return rd > ru + 1 ? rd - 1 : ru; // skip edges run their horizontal just above the target
  };
  // Shared lines, decided per source and target row:
  //  - a box with several links into one row gets one line that branches to
  //    all of them ("Julian -> his companies"), even when those companies
  //    have more lines coming in from others;
  //  - links that are their source's only one into that row share one line
  //    into their target ("Claudia, Tankred and Eberhard -> the Verwaltungs
  //    GmbH", the partners of a KG).
  // A shared line never carries links between several sources and several
  // targets at once.
  const toRow = new Map<string, number>(); // `${up}|${row}` -> links
  const rowKey = (e: GraphEdge) => `${e.up}|${rowOf(e.down)}|${dashedOf(e)}`;
  for (const e of edges) if (rowOf(e.down) > rowOf(e.up)) toRow.set(rowKey(e), (toRow.get(rowKey(e)) ?? 0) + 1);
  const outGroups = new Map<string, GraphEdge[]>();
  const inGroups = new Map<string, GraphEdge[]>();
  for (const e of edges) {
    if (rowOf(e.down) <= rowOf(e.up)) continue;
    if ((toRow.get(rowKey(e)) ?? 0) >= 2) {
      const k = rowKey(e);
      if (!outGroups.has(k)) outGroups.set(k, []);
      outGroups.get(k)!.push(e);
    } else {
      // sources further up join through a corridor (see lanes below)
      const k = `${e.down}|${dashedOf(e)}`;
      if (!inGroups.has(k)) inGroups.set(k, []);
      inGroups.get(k)!.push(e);
    }
  }
  const taken = new Set<string>();
  const groups = [
    ...[...outGroups.values()].map((g) => ({ g, kind: 'out' as const })),
    ...[...inGroups.values()].map((g) => ({ g, kind: 'in' as const })),
  ].sort((a, b) => b.g.length - a.g.length);
  const newItem = (es: GraphEdge[], kind: Item['kind']): Item => ({
    edges: es,
    gap: gapOf(es[0]),
    kind,
    // one label for the bus when all its edges say the same, else one per edge
    label: es.every((e) => labelFor(e) === labelFor(es[0])) ? labelFor(es[0]) : '',
    dashed: dashedOf(es[0]),
    width: widthOf(),
    hub: kind === 'out' ? es[0].up : kind === 'in' ? es[0].down : null,
    attach: new Map(),
    lane: 0,
    minX: 0,
    maxX: 0,
  });
  for (const { g, kind } of groups) {
    const free = g.filter((e) => !taken.has(e.id));
    if (free.length < 2) continue;
    free.forEach((e) => taken.add(e.id));
    items.push(newItem(free, kind));
  }
  for (const e of edges) if (!taken.has(e.id)) items.push(newItem([e], 'one'));

  // attachments, keyed per item; the hub end of a bus is one attachment
  const sides = new Map<string, Attach[]>(); // `${node}:${side}`
  const addAttach = (it: Item, key: string, a: Attach) => {
    it.attach.set(key, a);
    const sk = `${a.node}:${a.side}`;
    if (!sides.has(sk)) sides.set(sk, []);
    sides.get(sk)!.push(a);
  };
  const sameRow = (e: GraphEdge) => rowOf(e.up) === rowOf(e.down);
  for (const it of items) {
    const mean = (ids: string[]) => ids.reduce((s, id) => s + cx.get(id)!, 0) / ids.length;
    if (it.kind === 'out') {
      addAttach(it, 'hub', { node: it.hub!, side: 'bottom', dashed: it.dashed, toward: mean(it.edges.map((e) => e.down)) });
      for (const e of it.edges) addAttach(it, `${e.id}:down`, { node: e.down, side: 'top', dashed: it.dashed, toward: cx.get(it.hub!)! });
    } else if (it.kind === 'in') {
      addAttach(it, 'hub', { node: it.hub!, side: 'top', dashed: it.dashed, toward: mean(it.edges.map((e) => e.up)) });
      for (const e of it.edges) addAttach(it, `${e.id}:up`, { node: e.up, side: 'bottom', dashed: it.dashed, toward: cx.get(it.hub!)! });
    } else {
      const e = it.edges[0];
      addAttach(it, `${e.id}:up`, { node: e.up, side: 'bottom', dashed: it.dashed, toward: cx.get(e.down)! });
      addAttach(it, `${e.id}:down`, { node: e.down, side: sameRow(e) ? 'bottom' : 'top', dashed: it.dashed, toward: cx.get(e.up)! });
    }
  }
  for (const [sk, list] of sides) {
    const node = sk.slice(0, sk.lastIndexOf(':'));
    const w = size.get(node)!.w;
    const l = cx.get(node)! - w / 2;
    const r = cx.get(node)! + w / 2;
    list.sort((a, b) => a.toward - b.toward);
    if (list.length === 1) {
      list[0].x = Math.min(r - PORT_INSET, Math.max(l + PORT_INSET, list[0].toward));
    } else {
      const inset = Math.min(40, w * 0.16);
      list.forEach((a, i) => (a.x = l + inset + (i / (list.length - 1)) * (w - 2 * inset)));
    }
  }

  // Skip edges: pick a corridor through the rows they cross.
  const CORRIDOR_MARGIN = 8;
  const corridorUse = new Map<number, number>();
  const freeIn = (r: number, x: number) =>
    rows.get(r)!.every((id) => {
      const w = size.get(id)!.w;
      const c = cx.get(id)!;
      return x < c - w / 2 - CORRIDOR_MARGIN || x > c + w / 2 + CORRIDOR_MARGIN;
    });
  // `merge`: edges of one star going down the same corridor are one line
  const corridor = (from: number, to: number, x1: number, x2: number, merge = false): number => {
    const crossed: number[] = [];
    for (let r = from + 1; r < to; r++) if (rows.has(r)) crossed.push(r);
    if (crossed.every((r) => freeIn(r, x1))) return x1;
    const cands = new Set<number>([x2]);
    for (const r of crossed) {
      const spans = rows.get(r)!.map((id) => [cx.get(id)! - size.get(id)!.w / 2, cx.get(id)! + size.get(id)!.w / 2]).sort((a, b) => a[0] - b[0]);
      cands.add(spans[0][0] - CORRIDOR_MARGIN - 6);
      cands.add(spans[spans.length - 1][1] + CORRIDOR_MARGIN + 6);
      for (let i = 1; i < spans.length; i++) cands.add((spans[i - 1][1] + spans[i][0]) / 2);
    }
    const ok = [...cands].filter((x) => crossed.every((r) => freeIn(r, x)));
    if (!ok.length) return x1;
    const best = ok.sort((a, b) => Math.abs(a - x1) + Math.abs(a - x2) - (Math.abs(b - x1) + Math.abs(b - x2)))[0];
    // otherwise several edges down one corridor sit side by side
    if (merge) return best;
    const key = Math.round(best);
    const n = corridorUse.get(key) ?? 0;
    corridorUse.set(key, n + 1);
    return best + (n % 2 ? -1 : 1) * Math.ceil(n / 2) * 5;
  };

  // lanes per gap: overlapping horizontal spans get separate lanes
  interface Seg { gap: number; minX: number; maxX: number; set: (lane: number) => void }
  const segs: Seg[] = [];
  const edgeSkip = new Map<string, NonNullable<Item['skip']>>();
  for (const it of items) {
    const xs = [...it.attach.values()].map((a) => a.x!);
    it.minX = Math.min(...xs);
    it.maxX = Math.max(...xs);
    if (it.kind === 'in') {
      // the shared line spans the target port and where each edge reaches it:
      // its source port, or for an edge from further up, its corridor
      const hubX = it.attach.get('hub')!.x!;
      const reach: number[] = [hubX];
      for (const e of it.edges) {
        const x1 = it.attach.get(`${e.id}:up`)!.x!;
        if (rowOf(e.down) > rowOf(e.up) + 1) {
          const c = corridor(rowOf(e.up), rowOf(e.down), x1, hubX, true);
          const skip = { fromGap: rowOf(e.up), cx: c, laneA: null as number | null };
          edgeSkip.set(e.id, skip);
          if (Math.abs(c - x1) >= 1)
            segs.push({ gap: skip.fromGap, minX: Math.min(x1, c), maxX: Math.max(x1, c), set: (l) => (skip.laneA = l) });
          reach.push(c);
        } else reach.push(x1);
      }
      it.minX = Math.min(...reach);
      it.maxX = Math.max(...reach);
    }
    if (it.kind === 'out' && rowOf(it.edges[0].down) > rowOf(it.edges[0].up) + 1) {
      // one corridor for the trunk, then the shared line just above the targets
      const hubX = it.attach.get('hub')!.x!;
      const childXs = it.edges.map((e) => it.attach.get(`${e.id}:down`)!.x!);
      const mid = childXs.reduce((a, b) => a + b, 0) / childXs.length;
      const from = rowOf(it.edges[0].up);
      const c = corridor(from, rowOf(it.edges[0].down), hubX, mid, true);
      const skip = { fromGap: from, cx: c, laneA: null as number | null };
      it.skip = skip;
      if (Math.abs(c - hubX) >= 1)
        segs.push({ gap: from, minX: Math.min(hubX, c), maxX: Math.max(hubX, c), set: (l) => (skip.laneA = l) });
      it.minX = Math.min(c, ...childXs);
      it.maxX = Math.max(c, ...childXs);
    }
    const e = it.edges[0];
    if (it.kind === 'one' && rowOf(e.down) > rowOf(e.up) + 1) {
      const x1 = it.attach.get(`${e.id}:up`)!.x!;
      const x2 = it.attach.get(`${e.id}:down`)!.x!;
      const c = corridor(rowOf(e.up), rowOf(e.down), x1, x2);
      const skip = { fromGap: rowOf(e.up), cx: c, laneA: null as number | null };
      it.skip = skip;
      if (Math.abs(c - x1) >= 1)
        segs.push({ gap: skip.fromGap, minX: Math.min(x1, c), maxX: Math.max(x1, c), set: (l) => (skip.laneA = l) });
      it.minX = Math.min(c, x2);
      it.maxX = Math.max(c, x2);
    }
    segs.push({ gap: it.gap, minX: it.minX, maxX: it.maxX, set: (l) => (it.lane = l) });
  }
  const lanesInGap = new Map<number, number>();
  const byGap = new Map<number, Seg[]>();
  for (const sg of segs) {
    if (!byGap.has(sg.gap)) byGap.set(sg.gap, []);
    byGap.get(sg.gap)!.push(sg);
  }
  for (const [g, list] of byGap) {
    const ends: number[] = [];
    list
      .filter((sg) => sg.maxX - sg.minX >= 1)
      .sort((a, b) => a.minX - b.minX || b.maxX - a.maxX)
      .forEach((sg) => {
        let lane = ends.findIndex((end) => end + 10 < sg.minX);
        if (lane < 0) lane = ends.length;
        ends[lane] = sg.maxX;
        sg.set(lane);
      });
    lanesInGap.set(g, Math.max(1, ends.length));
  }

  // y: row tops, row heights and gap heights
  const rowTop = new Map<number, number>();
  const rowH = new Map<number, number>();
  let y = PAD + LABEL_H; // room for badges above the top row
  for (let i = 0; i < rowIds.length; i++) {
    const r = rowIds[i];
    rowTop.set(r, y);
    const h = Math.max(...rows.get(r)!.map((id) => size.get(id)!.h));
    rowH.set(r, h);
    const lanes = lanesInGap.get(r) ?? 0;
    y += h + (i < rowIds.length - 1 ? Math.max(MIN_GAP, BAND * 2 + (lanes - 1) * LANE) + growTop(r) + growBottom(r) : 0);
  }
  // same-row edges route through the gap below their row; the last row needs one too
  const lastRow = rowIds[rowIds.length - 1];
  if (byGap.has(lastRow)) y += BAND + (lanesInGap.get(lastRow)! - 1) * LANE;
  const height = y + PAD;

  const boxes = new Map<string, Box>();
  let minL = Infinity;
  let maxR = -Infinity;
  for (const n of nodes) {
    const s = size.get(n.id)!;
    const x = cx.get(n.id)! - s.w / 2;
    boxes.set(n.id, { x, y: rowTop.get(n.row)!, w: s.w, h: s.h });
    minL = Math.min(minL, x);
    maxR = Math.max(maxR, x + s.w);
  }
  const shift = PAD - minL;
  for (const b of boxes.values()) b.x += shift;
  for (const it of items) {
    for (const a of it.attach.values()) a.x! += shift;
    if (it.skip) it.skip.cx += shift;
    for (const e of it.edges) {
      const sk = edgeSkip.get(e.id);
      if (sk) sk.cx += shift;
    }
    it.minX += shift;
    it.maxX += shift;
  }
  const bottom = (id: string) => boxes.get(id)!.y + boxes.get(id)!.h;
  const top = (id: string) => boxes.get(id)!.y;

  const paths: EdgePath[] = [];
  const labels: EdgeLabel[] = [];
  const laneYIn = (gap: number, lane: number) => rowTop.get(gap)! + rowH.get(gap)! + BAND + growTop(gap) + lane * LANE;
  /**
   * Where along its line a label may sit: the vertical stretch between the box
   * it hangs off and where the line bends. `end` says which band of the gap
   * that is (top: under the upper row, bottom: over the lower row).
   */
  interface Slide { gap: number; end: 'top' | 'bottom'; from: number; to: number }
  const slides = new Map<EdgeLabel, Slide>();
  const half = (text: string) => labelHeight(text) / 2;
  const below = (l: Omit<EdgeLabel, 'y'> & { y?: number }, node: string, gap: number, bend: number) => {
    const h = half(l.text);
    const lab = { ...l, y: bottom(node) + 4 + h } as EdgeLabel;
    labels.push(lab);
    slides.set(lab, { gap, end: 'top', from: bottom(node) + 4 + h, to: Math.max(bottom(node) + 4 + h, bend - 3 - h) });
  };
  const above = (l: Omit<EdgeLabel, 'y'> & { y?: number }, node: string, gap: number, bend: number) => {
    const h = half(l.text);
    const lab = { ...l, y: top(node) - 4 - h } as EdgeLabel;
    labels.push(lab);
    slides.set(lab, { gap, end: 'bottom', from: top(node) - 4 - h, to: Math.min(top(node) - 4 - h, bend + 3 + h) });
  };
  // where an edge leaving `node` downwards first bends (its lane in that gap,
  // or the next row's top when it runs straight down a corridor)
  const firstBend = (it: Item, e: GraphEdge, laneY: number) => {
    const sk = edgeSkip.get(e.id) ?? it.skip;
    if (sk) return sk.laneA != null ? laneYIn(sk.fromGap, sk.laneA) : (rowTop.get(sk.fromGap + 1) ?? laneY);
    return laneY;
  };

  for (const it of items) {
    const g = it.gap;
    const gapTop = rowTop.get(g)! + rowH.get(g)!;
    const laneY = gapTop + BAND + growTop(g) + it.lane * LANE;
    for (const e of it.edges) {
      const up = it.kind === 'in' ? it.attach.get(`${e.id}:up`)! : it.kind === 'out' ? it.attach.get('hub')! : it.attach.get(`${e.id}:up`)!;
      const down = it.kind === 'out' ? it.attach.get(`${e.id}:down`)! : it.kind === 'in' ? it.attach.get('hub')! : it.attach.get(`${e.id}:down`)!;
      const y1 = bottom(up.node);
      const y2 = down.side === 'top' ? top(down.node) : bottom(down.node);
      const sk = edgeSkip.get(e.id) ?? it.skip;
      const d =
        sk && sk.laneA != null
          ? `M${up.x},${y1} V${laneYIn(sk.fromGap, sk.laneA)} H${sk.cx} V${laneY} H${down.x} V${y2}`
          : Math.abs(up.x! - down.x!) < 1 && down.side === 'top'
            ? `M${up.x},${y1} V${y2}`
            : `M${up.x},${y1} V${laneY} H${down.x} V${y2}`;
      paths.push({ id: e.id, d, dashed: it.dashed, width: it.width });
    }
    const ids = it.edges.map((e) => e.id);
    if (!it.label) {
      // a bus of differently labelled edges: each label at its edge's far end
      if (it.kind === 'one') continue;
      for (const e of it.edges) {
        const text = labelFor(e);
        if (!text) continue;
        if (it.kind === 'out') {
          const a = it.attach.get(`${e.id}:down`)!;
          above({ id: `${e.id}:l`, x: a.x!, text, anchor: 'center', edgeIds: [e.id] }, a.node, g, laneY);
        } else {
          const a = it.attach.get(`${e.id}:up`)!;
          const sk = edgeSkip.get(e.id);
          below({ id: `${e.id}:l`, x: a.x!, text, anchor: 'center', edgeIds: [e.id] }, a.node, sk ? sk.fromGap : g, firstBend(it, e, laneY));
        }
      }
      continue;
    }
    if (it.kind === 'out') {
      const a = it.attach.get('hub')!;
      below({ id: `${ids[0]}:l`, x: a.x! + 6, text: it.label, anchor: 'side', edgeIds: ids }, a.node, it.skip ? it.skip.fromGap : g, firstBend(it, it.edges[0], laneY));
    } else if (it.kind === 'in') {
      const a = it.attach.get('hub')!;
      above({ id: `${ids[0]}:l`, x: a.x! + 6, text: it.label, anchor: 'side', edgeIds: ids }, a.node, g, laneY);
    } else {
      const e = it.edges[0];
      const up = it.attach.get(`${e.id}:up`)!;
      const down = it.attach.get(`${e.id}:down`)!;
      if (sameRow(e)) {
        labels.push({ id: `${e.id}:l`, x: (up.x! + down.x!) / 2, y: laneY + 11, text: it.label, anchor: 'center', edgeIds: ids });
      } else if (rowOf(e.down) > rowOf(e.up) + 1) {
        below({ id: `${e.id}:l`, x: up.x! + 6, text: it.label, anchor: 'side', edgeIds: ids }, up.node, rowOf(e.up), firstBend(it, e, laneY));
      } else if (e.up === focal || rowOf(e.up) >= 0) {
        above({ id: `${e.id}:l`, x: down.x!, text: it.label, anchor: 'center', edgeIds: ids }, down.node, g, laneY);
      } else {
        below({ id: `${e.id}:l`, x: up.x!, text: it.label, anchor: 'center', edgeIds: ids }, up.node, g, laneY);
      }
    }
  }

  // Colliding labels drop (below an upper end) or rise (above a lower end) a level.
  const rect = (l: EdgeLabel) => {
    const w = labelWidth(l.text);
    const x0 = l.anchor === 'center' ? l.x - w / 2 : l.x;
    const h = labelHeight(l.text);
    return { x0, x1: x0 + w, y0: l.y - h / 2, y1: l.y + h / 2 };
  };
  const overlaps = (a: ReturnType<typeof rect>, b: ReturnType<typeof rect>) =>
    a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
  // boxes are obstacles too: a label never sits on a box
  const placedRects: ReturnType<typeof rect>[] = [...boxes.values()].map((b) => ({
    x0: b.x - 2,
    x1: b.x + b.w + 2,
    y0: b.y - 2,
    y1: b.y + b.h + 2,
  }));
  const need: Grow = new Map();
  const SLIDE_STEP = 3;
  for (const l of [...labels].sort((a, b) => a.y - b.y || a.x - b.x)) {
    const x0 = l.x;
    const y0 = l.y;
    const w = labelWidth(l.text);
    const sl = slides.get(l);
    // Slide along its own line, from the box towards the bend; a label beside
    // a trunk may also take the trunk's other side. Never sideways onto open
    // space — next to a neighbouring line a % reads as that line's.
    const ys: number[] = [];
    if (sl) {
      const dir = sl.to >= sl.from ? 1 : -1;
      for (let y = sl.from; dir > 0 ? y <= sl.to : y >= sl.to; y += dir * SLIDE_STEP) ys.push(y);
    } else ys.push(y0);
    const xs = l.anchor === 'side' ? [x0, x0 - (w + 12)] : [x0];
    let free = false;
    for (const y of ys) {
      for (const x of xs) {
        l.x = x;
        l.y = y;
        if (!placedRects.some((p) => overlaps(p, rect(l)))) {
          free = true;
          break;
        }
      }
      if (free) break;
    }
    if (free) placedRects.push(rect(l));
    else {
      // no room along the line: the next pass makes the gap taller; if it
      // already is as tall as it may get, shown only while highlighted
      l.x = x0;
      l.y = y0;
      l.hidden = true;
      if (sl) {
        const n = need.get(sl.gap) ?? { top: 0, bottom: 0 };
        const add = labelHeight(l.text) + 4;
        if (sl.end === 'top') n.top = Math.max(n.top, add);
        else n.bottom = Math.max(n.bottom, add);
        need.set(sl.gap, n);
      }
    }
  }

  const width = maxR - minL + PAD * 2;
  // Labels beside a trunk can run past the last box.
  const labelRight = Math.max(0, ...labels.map((l) => rect(l).x1 + PAD));
  return { layout: { boxes, paths, labels, segments: mergeSegments(paths), width: Math.max(width, labelRight), height }, need };
}

/** Cut "M x,y V y H x …" paths into straight stretches and merge overlaps per style. */
export function mergeSegments(paths: EdgePath[]): LineSegment[] {
  const spans = new Map<string, [number, number][]>(); // `h|y|dashed` / `v|x|dashed`
  const add = (key: string, a: number, b: number) => {
    if (Math.abs(a - b) < 0.01) return;
    if (!spans.has(key)) spans.set(key, []);
    spans.get(key)!.push(a < b ? [a, b] : [b, a]);
  };
  for (const p of paths) {
    let x = 0;
    let y = 0;
    for (const [, cmd, arg] of p.d.matchAll(/([MVH])\s*([^MVH]+)/g)) {
      if (cmd === 'M') {
        const [mx, my] = arg.split(',').map(Number);
        x = mx;
        y = my;
      } else if (cmd === 'V') {
        const ny = Number(arg);
        add(`v|${x.toFixed(1)}|${p.dashed}`, y, ny);
        y = ny;
      } else {
        const nx = Number(arg);
        add(`h|${y.toFixed(1)}|${p.dashed}`, x, nx);
        x = nx;
      }
    }
  }
  const out: LineSegment[] = [];
  for (const [key, list] of spans) {
    const [dir, at, dashed] = key.split('|');
    list.sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [];
    for (const sp of list) {
      const last = merged[merged.length - 1];
      if (last && sp[0] <= last[1] + 0.5) last[1] = Math.max(last[1], sp[1]);
      else merged.push([...sp]);
    }
    const d = merged
      .map(([a, b]) => (dir === 'h' ? `M${a},${at} H${b}` : `M${at},${a} V${b}`))
      .join(' ');
    out.push({ d, dashed: dashed === 'true' });
  }
  return out;
}

